import type { DateKey, HHMM } from './time';

/** Structural subset of the API's Plan; core stays independent of the generated client. */
export interface PlanLike {
  id: string;
  title: string;
  date: DateKey;
  time: HHMM | null;
  all_day: boolean;
  member_id: string | null;
  completed: boolean;
  deleted_at?: string | null;
}

/** 'everyone' | 'family' | <member id> */
export type PlanFilter = string;
export const EVERYONE = 'everyone';
export const FAMILY = 'family';

/** A member filter also shows whole-family plans; the family filter shows only those. */
export function matchesFilter(plan: { member_id: string | null }, filter: PlanFilter): boolean {
  if (filter === EVERYONE) return true;
  if (filter === FAMILY) return plan.member_id === null;
  return plan.member_id === filter || plan.member_id === null;
}

export function comparePlans(a: PlanLike, b: PlanLike): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
  const ta = a.time ?? '';
  const tb = b.time ?? '';
  if (ta !== tb) return ta < tb ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function plansForDay<T extends PlanLike>(plans: T[], day: DateKey, filter: PlanFilter): T[] {
  return plans
    .filter((p) => !p.deleted_at && p.date === day && matchesFilter(p, filter))
    .sort(comparePlans);
}

export type AgendaItem<T> =
  | { kind: 'now' }
  | { kind: 'allDayHeader' }
  | { kind: 'plan'; plan: T; past: boolean };

/**
 * Agenda rows for one day. On today, a NOW marker sits before the first timed plan later
 * than `now`, or after the last one when everything is past.
 */
export function buildAgenda<T extends PlanLike>(dayPlans: T[], opts: { isToday: boolean; now: HHMM }): AgendaItem<T>[] {
  const out: AgendaItem<T>[] = [];
  const allDay = dayPlans.filter((p) => p.all_day);
  const timed = dayPlans.filter((p) => !p.all_day);
  if (allDay.length) {
    out.push({ kind: 'allDayHeader' });
    for (const p of allDay) out.push({ kind: 'plan', plan: p, past: false });
  }
  let nowPlaced = !opts.isToday;
  for (const p of timed) {
    const t = p.time ?? '00:00';
    if (!nowPlaced && t > opts.now) {
      out.push({ kind: 'now' });
      nowPlaced = true;
    }
    out.push({ kind: 'plan', plan: p, past: opts.isToday && t <= opts.now });
  }
  if (!nowPlaced && timed.length) out.push({ kind: 'now' });
  return out;
}

export function nextUp<T extends PlanLike>(dayPlans: T[], now: HHMM): T | undefined {
  for (const p of dayPlans) if (!p.all_day && !p.completed && (p.time ?? '') > now) return p;
  return undefined;
}

export function planCountLabel(n: number): string {
  return `${n} ${n === 1 ? 'plan' : 'plans'}`;
}

/** Default time for a new plan: the next full hour, capped at 23:00. */
export function defaultPlanTime(now: HHMM): HHMM {
  const h = Math.min(Number(now.slice(0, 2)) + 1, 23);
  return `${h < 10 ? '0' : ''}${h}:00`;
}

/** Merge a delta feed into a cached list (server wins; soft-deleted rows are dropped). */
export function mergeChanges<T extends PlanLike>(current: T[], changes: T[]): T[] {
  const byId: Record<string, T> = {};
  for (const p of current) byId[p.id] = p;
  for (const c of changes) {
    if (c.deleted_at) delete byId[c.id];
    else byId[c.id] = c;
  }
  return Object.keys(byId).map((k) => byId[k]!).sort(comparePlans);
}
